/**
 * Payment Run (AP).
 *
 * Winnie works her payables out of the Bills list in Xero: everything owed, grouped by
 * the day she plans to pay it, a running total per group, and an export she can hand to
 * the bank. This is that list, for Direct Auto's own data.
 *
 * Two honesties are built in and both are visible on screen:
 *   - a vehicle payment's status belongs to the approval workflow, so it is read only
 *     here. The planned date is not; deciding when to pay is not paying.
 *   - only vehicle payments can go into an ABA file, because the seller's account is on
 *     the deal. We do not hold supplier bank details, so supplier bills export to CSV
 *     and the screen says so rather than producing a bank file that pays nobody.
 */

import { useState, useEffect, useMemo } from 'react'
import { User } from '../../types'
import { DateRange, ALL_TIME } from '../../lib/dateRange'
import { errText } from '../../lib/errText'
import ReportRangeFilter from './ReportRangeFilter'
import {
  Payable,
  PayableStatus,
  AbaSettings,
  EMPTY_ABA_SETTINGS,
  loadPayables,
  setPlannedDate,
  markBillPaid,
  markBillUnpaid,
  payablesCsv,
  loadAbaSettings,
  saveAbaSettings,
} from '../../services/payablesService'
import { buildAbaFile, validateAba, abaFileName, AbaPayment } from '../../lib/aba'
import './PayablesPanel.css'
import { toast } from '../Toast'

interface PayablesPanelProps {
  user: User
}

type StatusFilter = 'all' | 'awaiting_payment' | 'paid' | 'void'

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'awaiting_payment', label: 'Awaiting payment' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
]

const statusLabel = (s: PayableStatus) =>
  s === 'paid' ? 'Paid' : s === 'void' ? 'Void' : 'Awaiting payment'

const currency = (n: number) =>
  n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 })

const prettyDate = (iso: string | null) => {
  if (!iso) return '-'
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00Z')
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
}

const todayIso = () => new Date().toISOString().slice(0, 10)

function download(name: string, body: string, mime: string) {
  const blob = new Blob([body], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export default function PayablesPanel({ user }: PayablesPanelProps) {
  const [range, setRange] = useState<DateRange>(ALL_TIME)
  const [status, setStatus] = useState<StatusFilter>('awaiting_payment')
  const [rows, setRows] = useState<Payable[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [bulkDate, setBulkDate] = useState('')
  const [busy, setBusy] = useState(false)
  const [showBank, setShowBank] = useState(false)
  const [aba, setAba] = useState<AbaSettings>(EMPTY_ABA_SETTINGS)
  const [abaSaving, setAbaSaving] = useState(false)
  const [abaProblems, setAbaProblems] = useState<string[]>([])

  const isAdmin = user.role === 'admin'

  useEffect(() => {
    let live = true
    setLoading(true)
    setError(null)
    Promise.all([loadPayables(range), loadAbaSettings().catch(() => EMPTY_ABA_SETTINGS)])
      .then(([payables, settings]) => {
        if (!live) return
        setRows(payables)
        setAba(settings)
      })
      .catch((err) => {
        if (!live) return
        setError(errText(err, 'Could not load the payables list.'))
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [range.from, range.to])

  const visible = useMemo(
    () => (status === 'all' ? rows : rows.filter((r) => r.status === status)),
    [rows, status],
  )

  const groups = useMemo(() => {
    const byDate = new Map<string, Payable[]>()
    visible.forEach((r) => {
      const key = r.plannedDate || ''
      if (!byDate.has(key)) byDate.set(key, [])
      const bucket = byDate.get(key) as Payable[]
      bucket.push(r)
    })
    const keys = [...byDate.keys()].sort((a, b) => {
      if (!a) return 1
      if (!b) return -1
      return a.localeCompare(b)
    })
    return keys.map((key) => {
      const items = byDate.get(key) as Payable[]
      return {
        key,
        label: key ? prettyDate(key) : 'No planned date',
        items,
        total: items.reduce((s, r) => s + r.amount, 0),
      }
    })
  }, [visible])

  const selectedRows = useMemo(() => visible.filter((r) => selected[r.id]), [visible, selected])
  const grandTotal = visible.reduce((s, r) => s + r.amount, 0)

  const refresh = async () => {
    const fresh = await loadPayables(range)
    setRows(fresh)
  }

  const toggle = (id: string) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }))

  const toggleGroup = (items: Payable[], on: boolean) =>
    setSelected((prev) => {
      const next = { ...prev }
      items.forEach((r) => {
        next[r.id] = on
      })
      return next
    })

  const applyPlannedDate = async () => {
    if (!selectedRows.length) {
      setError('Tick the payments you want to schedule first.')
      return
    }
    try {
      setBusy(true)
      setError(null)
      for (const row of selectedRows) {
        await setPlannedDate(row.kind, row.id, bulkDate || null)
      }
      await refresh()
      const many = selectedRows.length === 1 ? '' : 's'
      setNotice(
        bulkDate
          ? 'Planned ' + selectedRows.length + ' payment' + many + ' for ' + prettyDate(bulkDate) + '.'
          : 'Cleared the planned date on ' + selectedRows.length + ' payment' + many + '.',
      )
    } catch (err) {
      setError(errText(err, 'Could not save the planned date.'))
    } finally {
      setBusy(false)
    }
  }

  const changeOneDate = async (row: Payable, value: string) => {
    try {
      setError(null)
      await setPlannedDate(row.kind, row.id, value || null)
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, plannedDate: value || null } : r)))
    } catch (err) {
      setError(errText(err, 'Could not save the planned date.'))
    }
  }

  const togglePaid = async (row: Payable) => {
    if (row.statusLocked) return
    try {
      setBusy(true)
      setError(null)
      if (row.status === 'paid') {
        await markBillUnpaid(row.id)
      } else {
        await markBillPaid(row.id, row.plannedDate || todayIso(), row.reference)
      }
      await refresh()
    } catch (err) {
      setError(errText(err, 'Could not update the bill.'))
    } finally {
      setBusy(false)
    }
  }

  const exportCsv = () => {
    const set = selectedRows.length ? selectedRows : visible
    if (!set.length) {
      setError('There is nothing in the list to export.')
      return
    }
    download('DirectAuto-payables-' + todayIso() + '.csv', payablesCsv(set), 'text/csv;charset=utf-8')
    setNotice('Exported ' + set.length + ' transaction' + (set.length === 1 ? '' : 's') + '.')
  }

  const exportAba = () => {
    setAbaProblems([])
    const chosen = selectedRows.length
      ? selectedRows
      : visible.filter((r) => r.status === 'awaiting_payment')
    const vehicles = chosen.filter((r) => r.kind === 'vehicle')
    const skipped = chosen.length - vehicles.length

    if (!vehicles.length) {
      setAbaProblems([
        'A bank file needs an account to pay into. None of the selected rows is a vehicle payment with the seller bank details recorded on the deal.',
      ])
      return
    }

    const scheduled = vehicles.find((v) => v.plannedDate)
    const processingDate = scheduled && scheduled.plannedDate ? scheduled.plannedDate : todayIso()

    const payments: AbaPayment[] = vehicles.map((v) => ({
      bsb: v.bsb || '',
      accountNumber: v.accountNumber || '',
      accountTitle: v.accountName || v.payee,
      amountCents: Math.round(v.amount * 100),
      reference: (v.reference || v.stockNumber || v.description).slice(0, 18),
    }))

    const header = {
      bankCode: aba.bankCode,
      userName: aba.userName,
      userId: aba.userId,
      description: 'VEHICLE PAY',
      processingDate,
      traceBsb: aba.traceBsb,
      traceAccount: aba.traceAccount,
      remitterName: aba.remitterName,
    }

    const problems = validateAba(header, payments)
    if (problems.length) {
      setAbaProblems(problems.map((p) => p.message))
      return
    }

    try {
      download(abaFileName(processingDate), buildAbaFile(header, payments), 'text/plain;charset=utf-8')
      const tail = skipped
        ? '. ' + skipped + ' supplier bill' + (skipped === 1 ? '' : 's') + ' left out, we do not hold their bank details.'
        : '.'
      setNotice('ABA file built for ' + vehicles.length + ' payment' + (vehicles.length === 1 ? '' : 's') + tail)
    } catch (err) {
      setAbaProblems([errText(err, 'Could not build the file.')])
    }
  }

  const saveBank = async () => {
    try {
      setAbaSaving(true)
      setError(null)
      await saveAbaSettings(aba)
      toast.saved()
      setShowBank(false)
    } catch (err) {
      setError(errText(err, 'Could not save the bank file details.'))
    } finally {
      setAbaSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="ap-panel">
        <div className="ap-loading">Loading payables...</div>
      </div>
    )
  }

  return (
    <div className="ap-panel">
      <div className="ap-head">
        <h2>Accounts payable</h2>
        <p className="ap-sub">
          Everything Direct Auto owes: vehicle purchases and supplier bills, grouped by the day
          they are planned to be paid.
        </p>
      </div>

      {notice && (
        <div className="ap-alert ap-alert-ok" onClick={() => setNotice(null)}>
          <span>{notice}</span>
        </div>
      )}
      {error && (
        <div className="ap-alert ap-alert-bad" onClick={() => setError(null)}>
          <span>{error}</span>
        </div>
      )}

      <ReportRangeFilter value={range} onChange={setRange} />

      <div className="ap-toolbar">
        <label className="ap-field">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="ap-field">
          <span>Planned payment date</span>
          <input type="date" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} />
        </label>

        <button className="ap-btn" onClick={applyPlannedDate} disabled={busy}>
          Add planned date
        </button>
        <button className="ap-btn" onClick={exportCsv}>
          Export transactions
        </button>
        <button className="ap-btn ap-btn-primary" onClick={exportAba}>
          Export ABA file
        </button>
        {isAdmin && (
          <button className="ap-btn ap-btn-quiet" onClick={() => setShowBank(!showBank)}>
            {showBank ? 'Hide bank file setup' : 'Bank file setup'}
          </button>
        )}
        <span className="ap-count">
          {visible.length} item{visible.length === 1 ? '' : 's'} &middot; {currency(grandTotal)}
          {selectedRows.length ? ' \u00b7 ' + selectedRows.length + ' selected' : ''}
        </span>
      </div>

      {abaProblems.length > 0 && (
        <div className="ap-alert ap-alert-bad">
          <strong>The bank file was not written.</strong>
          <ul>
            {abaProblems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {showBank && isAdmin && (
        <div className="ap-bank">
          <h3>Bank file setup</h3>
          <p className="ap-sub">
            These are Direct Auto&rsquo;s own details and they go in the header of every ABA file.
            Your bank issues the direct entry user ID; type these in yourself.
          </p>
          <div className="ap-bank-grid">
            <label>
              <span>Bank abbreviation</span>
              <input
                value={aba.bankCode}
                placeholder="WBC"
                maxLength={3}
                onChange={(e) => setAba({ ...aba, bankCode: e.target.value.toUpperCase() })}
              />
            </label>
            <label>
              <span>Account name we bank under</span>
              <input
                value={aba.userName}
                maxLength={26}
                onChange={(e) => setAba({ ...aba, userName: e.target.value })}
              />
            </label>
            <label>
              <span>Direct entry user ID (6 digits)</span>
              <input
                value={aba.userId}
                maxLength={6}
                onChange={(e) => setAba({ ...aba, userId: e.target.value })}
              />
            </label>
            <label>
              <span>Our BSB</span>
              <input
                value={aba.traceBsb}
                maxLength={7}
                onChange={(e) => setAba({ ...aba, traceBsb: e.target.value })}
              />
            </label>
            <label>
              <span>Our account number</span>
              <input
                value={aba.traceAccount}
                maxLength={9}
                onChange={(e) => setAba({ ...aba, traceAccount: e.target.value })}
              />
            </label>
            <label>
              <span>Name the payee sees</span>
              <input
                value={aba.remitterName}
                maxLength={16}
                onChange={(e) => setAba({ ...aba, remitterName: e.target.value })}
              />
            </label>
          </div>
          <button className="ap-btn ap-btn-primary" onClick={saveBank} disabled={abaSaving}>
            {abaSaving ? 'Saving...' : 'Save bank details'}
          </button>
        </div>
      )}

      {groups.length === 0 ? (
        <div className="ap-empty">Nothing payable in this range.</div>
      ) : (
        groups.map((group) => {
          const allOn = group.items.every((r) => selected[r.id])
          return (
            <div className="ap-group" key={group.key || 'none'}>
              <div className="ap-group-head">
                <label className="ap-check">
                  <input
                    type="checkbox"
                    checked={allOn}
                    onChange={(e) => toggleGroup(group.items, e.target.checked)}
                  />
                  <strong>{group.label}</strong>
                </label>
                <span className="ap-group-total">
                  {group.items.length} item{group.items.length === 1 ? '' : 's'} &middot;{' '}
                  {currency(group.total)}
                </span>
              </div>
              <div className="ap-table-wrap">
                <table className="ap-table">
                  <thead>
                    <tr>
                      <th />
                      <th>From</th>
                      <th>Reference</th>
                      <th>Vehicle</th>
                      <th>Date</th>
                      <th>Due date</th>
                      <th>Planned date</th>
                      <th className="ap-right">Amount</th>
                      <th className="ap-right">GST</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={!!selected[row.id]}
                            onChange={() => toggle(row.id)}
                          />
                        </td>
                        <td>
                          <div className="ap-payee">{row.payee}</div>
                          <div className="ap-kind">
                            {row.kind === 'vehicle' ? 'Vehicle purchase' : 'Supplier bill'}
                          </div>
                        </td>
                        <td>{row.reference || '-'}</td>
                        <td>
                          <div>{row.description}</div>
                          {row.vin && <div className="ap-kind">{row.vin}</div>}
                        </td>
                        <td>{prettyDate(row.billDate)}</td>
                        <td>{prettyDate(row.dueDate)}</td>
                        <td>
                          <input
                            type="date"
                            className="ap-date"
                            value={row.plannedDate || ''}
                            onChange={(e) => changeOneDate(row, e.target.value)}
                          />
                        </td>
                        <td className="ap-right">{currency(row.amount)}</td>
                        <td className="ap-right">{row.gst ? currency(row.gst) : '-'}</td>
                        <td>
                          <span className={'ap-pill ap-pill-' + row.status}>{statusLabel(row.status)}</span>
                        </td>
                        <td>
                          {row.statusLocked ? (
                            <span
                              className="ap-kind"
                              title="A vehicle payment is released through the approval workflow, not from this list."
                            >
                              Approval workflow
                            </span>
                          ) : (
                            <button
                              className="ap-btn ap-btn-small"
                              onClick={() => togglePaid(row)}
                              disabled={busy}
                            >
                              {row.status === 'paid' ? 'Mark unpaid' : 'Mark paid'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}
